type SessionBookingLike = {
  date?: string | null
  cancelled_at?: string | null
  session?: { type?: string | null } | null
}

type BookingWithStatus = {
  date?: string | null
  attended?: boolean | null
  cancelled_at?: string | null
}

// Scheduled bookings first (they're the ones an internal user might still act
// on), then resolved ones, with cancelled bookings last.
const STATUS_RANK = {
  scheduled: 0,
  attended: 1,
  notAttended: 2,
  cancelled: 3,
} as const

function statusRank(booking: BookingWithStatus): number {
  if (booking.cancelled_at) return STATUS_RANK.cancelled
  if (booking.attended == null) return STATUS_RANK.scheduled
  return booking.attended ? STATUS_RANK.attended : STATUS_RANK.notAttended
}

/** Sorts bookings by status (scheduled, attended, not attended, cancelled), then by date within each status. */
export function sortBookingsByStatusAndDate<T extends BookingWithStatus>(bookings: T[]): T[] {
  return [...bookings].sort((a, b) => {
    const rankDiff = statusRank(a) - statusRank(b)
    if (rankDiff !== 0) return rankDiff

    const aTime = a.date ? Date.parse(a.date) : NaN
    const bTime = b.date ? Date.parse(b.date) : NaN
    if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0
    if (Number.isNaN(aTime)) return 1
    if (Number.isNaN(bTime)) return -1
    return aTime - bTime
  })
}

/** Bookings of the given session type that haven't been cancelled. */
export function selectActiveBookings<T extends SessionBookingLike>(
  bookings: T[],
  sessionType: string,
): T[] {
  return bookings.filter((b) => b.session?.type === sessionType && !b.cancelled_at)
}

/** The earliest-dated booking, ignoring rows without a date. Null if none qualify. */
export function findEarliestBooking<T extends SessionBookingLike>(bookings: T[]): T | null {
  const withDates = bookings.filter((b) => !!b.date)
  if (withDates.length === 0) return null

  return withDates.reduce((min, b) => {
    const t = Date.parse(b.date!)
    const minT = Date.parse(min.date!)
    if (Number.isNaN(t)) return min
    if (Number.isNaN(minT) || t < minT) return b
    return min
  }, withDates[0]!)
}
